/**
 * Cenário aberto — agora com o simulador de verdade dentro.
 *
 * Esta tela é a costura entre a casca e o simulador legado. O que ela faz, em ordem:
 *   1. abre o cenário e os irmãos (a comparação do EL-123 precisa dos `lev` deles);
 *   2. carrega as premissas macro da incorporadora;
 *   3. abre a sessão de rascunho e restaura o que ficou digitado e não publicado;
 *   4. monta o simulador.
 *
 * DESDE 09/10/2026 (lote de 09/10/2026, item 4) ela é também o lugar dos cenários: a tela
 * intermediária com a lista (EL-176) deixou de existir, e o título — o nome do cenário —
 * virou o seletor EL-003 (`ui/seletor-cenario.ts`), com trocar, criar, renomear, copiar,
 * arquivar, restaurar e (Admin Hype) excluir de vez. As ações estão em `acoes-cenario.ts`.
 * O endereço do empreendimento (`/inc/<e>/emp/<d>`) continua valendo e resolve para cá
 * (`telas/empreendimento.ts`).
 *
 * ⚠ O EL-178 ("Conferência do cálculo") foi REMOVIDO: era o andaime que provava o ciclo
 * banco → mapeamento → motor → tela enquanto não havia simulador. Agora o simulador
 * mostra os mesmos números com muito mais contexto, e dois painéis dizendo a mesma coisa
 * só criariam a dúvida de qual está certo.
 */
import { h } from '../dom';
import { icone } from '../icones';
import type { ItemNav, Vista } from '../casca';
import { avisar, confirmar } from '../ui/aviso';
import { cadastroAoAbrir, lgcAoAbrir, textosDivergenciaLgc } from '../lgc-rascunho';
import { mesmaEntrada } from '../legado/impressao-entrada';
import { moeda } from '../../../../engine/formato';
import { criarSeletorCenario } from '../ui/seletor-cenario';
import { acoesDeCenario, tomarSementeDeRascunho, verSementeDeRascunho } from './acoes-cenario';
import { abrirDialogoRelatorio, fecharDialogoRelatorio } from '../relatorio/dialogo';
import {
  abrirCenario, carregarPremissas, listarCenarios, listarCenariosArquivados, listarEmpreendimentos, listarEmpresas,
  versaoDoCenario,
} from '../../../../packages/dados/src/repositorios';
import type { CenarioResumo } from '../../../../packages/dados/src/repositorios';
import {
  anotarEdicao, atualizarVersaoBase, carregarRascunho, cenariosComRascunho, descartarRascunho, encerrarRascunho, gravarAgora,
  iniciarRascunho, temRascunhoNaoPublicado,
} from '../../../../packages/dados/src/rascunho';
import { podeEditar } from '../../../../packages/dados/src/sessao';
import type { Perfil } from '../../../../packages/dados/src/sessao';
import { montar, desmontar, capturarVista, atualizar } from '../legado/montagem';
import type { CenarioLegado, VistaSimulador } from '../legado/montagem';

/** Converte o par (resumo, entrada) para o formato que o simulador legado usa. */
function paraLegado(c: CenarioResumo, lev: CenarioLegado['lev']): CenarioLegado {
  return { id: c.id, empId: c.empreendimentoId, nome: c.nome, lev, temFluxo: c.temFluxo };
}

/* ----------------------------------------------------------------------------
 * MEMÓRIA DE SESSÃO — vive enquanto a aba estiver aberta, e só aqui.
 *
 * `vistaPorCenario`: onde a pessoa parou. Trocar de tela desmontava o simulador e a
 * próxima montagem começava do zero — quem estava com a tabela de unidades aberta a
 * encontrava fechada ao voltar.
 *
 * `jaAvisadoDoRascunho`: o "recuperamos o que você digitou" existe para o reencontro —
 * abrir hoje o que ficou pela metade ontem. Repetido a cada ida e volta ele vira ruído,
 * e ruído repetido é como se para de ler aviso. Uma vez por cenário, por sessão.
 * ---------------------------------------------------------------------------- */
const vistaPorCenario = new Map<string, VistaSimulador>();
const jaAvisadoDoRascunho = new Set<string>();

/* Lote 4 (item 6): "Manter o que digitei" respondido nesta aba, por cenário — a MESMA dupla de
   valores (rascunho, oficial) não é perguntada de novo a cada ida e volta. Outra dupla (o oficial
   mudou de novo, ou o digitado) pergunta outra vez. */
const lgcRespondido = new Map<string, string>();
const chaveLgc = (d: { doRascunho: number; oficial: number }): string =>
  `${Math.round(d.doRascunho * 100)}|${Math.round(d.oficial * 100)}`;
const lgcJaRespondido = (cenarioId: string, d: { doRascunho: number; oficial: number }): boolean =>
  lgcRespondido.get(cenarioId) === chaveLgc(d);
const lembrarLgcRespondido = (cenarioId: string, d: { doRascunho: number; oficial: number }): void => {
  lgcRespondido.set(cenarioId, chaveLgc(d));
};

/* ----------------------------------------------------------------------------
 * O ÚLTIMO CENÁRIO ABERTO EM CADA EMPREENDIMENTO (09/10/2026)
 *
 * Entrar no empreendimento abre o cenário onde a pessoa parou (decisão declarada no plano
 * do item 4). É conforto, não dado: mora no navegador — em outro computador cai no mais
 * antigo, e sem armazenamento (aba privada, bloqueio) também. Por isso try/catch nos dois
 * lados, e nunca um erro por causa disto.
 * ---------------------------------------------------------------------------- */
const CHAVE_ULTIMO = 'fincast.ultimoCenario.';

export function ultimoCenarioAberto(empreendimentoId: string): string | null {
  try {
    return localStorage.getItem(CHAVE_ULTIMO + empreendimentoId);
  } catch {
    return null;
  }
}

function lembrarUltimoCenario(empreendimentoId: string, cenarioId: string): void {
  try {
    localStorage.setItem(CHAVE_ULTIMO + empreendimentoId, cenarioId);
  } catch {
    /* armazenamento bloqueado: entrar no empreendimento abre o mais antigo */
  }
}

/* ----------------------------------------------------------------------------
 * A LISTA QUE O RESOLVEDOR ACABOU DE LER
 *
 * Entrar pelo empreendimento lê os cenários para ESCOLHER qual abrir, e em seguida esta
 * tela os leria de novo para o seletor. A lista vem passada adiante, uma vez, e só se for
 * de segundos atrás — uma lista velha esconderia o cenário que um colega acabou de criar.
 * ---------------------------------------------------------------------------- */
let listaEntregue: { empreendimentoId: string; cenarios: CenarioResumo[]; ate: number } | null = null;

export function entregarListaDeCenarios(empreendimentoId: string, cenarios: CenarioResumo[]): void {
  listaEntregue = { empreendimentoId, cenarios, ate: Date.now() + 5_000 };
}

function tomarListaEntregue(empreendimentoId: string): CenarioResumo[] | null {
  const l = listaEntregue;
  listaEntregue = null;
  return l !== null && l.empreendimentoId === empreendimentoId && Date.now() < l.ate ? l.cenarios : null;
}

/* ----------------------------------------------------------------------------
 * EL-190 — A PUBLICAÇÃO DE OUTRA PESSOA CHEGA SOZINHA (07/08/2026)
 *
 * Pedido literal: "se uma pessoa salvar dados num cenário, automaticamente essas
 * alterações apareçam pra mim e eu não tenha que abrir novamente o mesmo cenário".
 *
 * O que se sincroniza é o PUBLICADO, e só ele. O rascunho é privado por política de banco
 * (`rasc_sel` exige `usuario_id = auth.uid()`) e por decisão de projeto: o que o outro
 * está digitando agora não é para aparecer aqui — só o que ele decidiu publicar.
 *
 * SONDA, não websocket. Ligar o Realtime do Supabase custa uma migration, uma seção nova
 * na fotografia do esquema (senão a conferência dev↔produção nasce cega no que foi ligado)
 * e um estado de socket que o monitor de conexão não sabe medir — ele instrumenta o
 * `fetch`, e diria "online" com o socket morto. Duas colunas a cada 20 segundos custam
 * praticamente nada e não criam nenhuma dessas dívidas.
 *
 * A REGRA QUE PROTEGE O TRABALHO: se eu tenho alteração minha ainda não publicada, a tela
 * NÃO troca sozinha. Trocar apagaria o que eu estava fazendo — e `carregarRascunho` já
 * descarta o rascunho cuja versão-base ficou para trás. Nesse caso o aviso aparece e a
 * decisão é minha.
 *
 * E DESDE 09/10/2026 a sonda também diz quando o cenário SAIU daqui — arquivado por um
 * colega pelo seletor, ou excluído de vez pelo administrador. Antes ela ficava calada, e a
 * próxima publicação falhava com "alguém publicou enquanto você editava", que era falso.
 * ---------------------------------------------------------------------------- */
const INTERVALO_SONDA_MS = 20_000;

export async function telaCenario(perfil: Perfil, cenarioId: string): Promise<Vista> {
  /* ⚠ ATÉ O `return`, NADA COM EFEITO FORA DESTA FUNÇÃO (revisão de 09/10/2026). Esta vista pode
     ser DESCARTADA sem nunca chegar à tela — o F5 desenha duas vezes, o "Desfazer" reabre, trocar
     e voltar rápido —, e a descartada não passa pelo `aoSair`. Aqui só se LÊ; montar o simulador,
     a sonda, o ouvinte do documento e a sessão de rascunho começam no `aoMostrar`, quando a casca
     adota a vista (ver `Vista.aoMostrar`). Antes, a sonda órfã de uma vista descartada anunciava
     "outra pessoa publicou" sobre a publicação de quem estava na tela. */
  const aberto = await abrirCenario(cenarioId);
  const { cenario } = aberto;
  /* Muda a cada publicação: a versão é a trava otimista e precisa acompanhar. */
  let cenarioCorrente: CenarioResumo = cenario;

  const listaPronta = tomarListaEntregue(cenario.empreendimentoId);
  const [empresas, irmaos, listaCenarios, premissas] = await Promise.all([
    listarEmpresas(),
    listarEmpreendimentos(cenario.empresaId),
    listaPronta ?? listarCenarios(cenario.empreendimentoId),
    carregarPremissas(cenario.empresaId),
  ]);

  const empresaDoCenario = empresas.find((e) => e.id === cenario.empresaId);
  const nomeEmpresa = empresaDoCenario?.nome ?? 'Incorporadora';
  const nomeEmp = irmaos.find((e) => e.id === cenario.empreendimentoId)?.nome ?? 'Empreendimento';
  /* ADM-2: a situação lida AGORA (esta lista) vale sobre o perfil em memória — ver `sessao.ts`.
     Fora da lista (`undefined`), decide o perfil, como antes. */
  const editavel = podeEditar(perfil, cenario.empresaId, empresaDoCenario?.ativa);

  /* Arquivado e aberto mesmo assim: só quem tem poderes de gestor chega aqui (`cen_sel` reabre
     o arquivado para o gestor — e, desde a 0031, para o Admin Fincast, que tem os mesmos
     poderes em toda incorporadora ativa), por um link antigo. A tela diz o que é e não monta o
     simulador — editar não iria a lugar nenhum (o banco recusa) e o que ele mostraria não ocupa
     vaga nem compara. */
  const arquivado = cenario.arquivadoEm !== null;

  /* Os irmãos entram para a comparação de cenários. Cada um custa uma ida ao servidor,
     e o protótipo limita a 5 por empreendimento — então é um teto conhecido, não uma
     lista que pode crescer sem limite. Em paralelo, e sem derrubar a tela se um falhar:
     um cenário irmão ilegível é motivo para comparar sem ele, não para não abrir nada.
     Cada irmão leva o cadastro DELE, como o banco o devolveu (`emp`): desde a 0032 o cadastro é
     de cada cenário, e é sobre ele que a coluna do irmão na Comparação é derivada — ver
     `CenarioLegado.emp`. Publicar o aberto não muda o cadastro de irmão nenhum. */
  const outros = arquivado ? [] : listaCenarios.filter((c) => c.id !== cenarioId);
  const carregados = await Promise.all(
    outros.map((c) =>
      abrirCenario(c.id)
        .then((a): CenarioLegado => ({ ...paraLegado(c, a.entrada.lev), emp: a.entrada.empreendimento }))
        .catch(() => null),
    ),
  );
  const irmaosOk = carregados.filter((c): c is CenarioLegado => c !== null);
  const falharam = carregados.length - irmaosOk.length;

  /* O rascunho: aqui só LEITURA. A sessão (que grava) começa no `aoMostrar`.
     O que estava pendente na fila é descarregado ANTES de ler — antes, quem fazia isso era
     `iniciarRascunho`, que vinha primeiro; sem isto, voltar a um cenário segundos depois de
     editá-lo leria do servidor uma versão sem as últimas edições. O rascunho pessoal vem
     DEPOIS do publicado e ganha dele — é o trabalho que o usuário não terminou. */
  let lev = aberto.entrada.lev;
  let empreendimento = aberto.entrada.empreendimento;
  let restaurado = false;
  /* D25 = A (12/10/2026): a regra do LGC do lote 4 vale SÓ para o rascunho gravado antes da 0032
     (formato 1). `lgcOrigem` é o LGC oficial de quando ele nasceu — para separar o DIGITADO da
     fotografia (ver lgc-rascunho.ts); `undefined` = rascunho ainda mais antigo, sem o campo. */
  let formatoDoRascunho: 1 | 2 | null = null;
  let lgcOrigem: number | undefined;
  /* A cópia que não pôde ser publicada (faltava campo obrigatório) chega com o que estava
     na tela do original — ver `acoes-cenario.ts`. Aqui só se OLHA: quem consome é a tela adotada.
     O cadastro dela é o da tela do original e vale inteiro (é o rascunho da cópia, formato 2). */
  const semente = editavel && !arquivado ? verSementeDeRascunho(cenarioId) : null;
  if (editavel && !arquivado) {
    if (semente !== null) {
      lev = structuredClone(semente.entrada.lev);
      empreendimento = structuredClone(semente.entrada.empreendimento);
    } else {
      await gravarAgora();
      /* CAL-3: "vencido" contra a versão que ESTA tela abriu — uma publicação de outra pessoa entre o
         abrir e esta leitura não pode esconder o rascunho feito sobre ela (a sonda trará a versão nova) */
      const guardado = await carregarRascunho(cenarioId, cenario.versao);
      if (guardado !== null) {
        lev = guardado.entrada.lev;
        empreendimento = guardado.entrada.empreendimento;
        restaurado = true;
        formatoDoRascunho = guardado.formato;
        lgcOrigem = guardado.lgcOficialNaOrigem;
      }
    }
  }

  /* O rascunho carrega uma FOTOGRAFIA do cadastro junto.
       · O NOME do empreendimento muda só por "Renomear" (a publicação não o leva): a fotografia
         mostraria, e o arquivo exportado levaria, o nome de antes de um renomear. O publicado é a
         autoridade e vem por último — em qualquer formato.
       · O resto do cadastro, inclusive o LGC: desde a 0032 ele é DESTE cenário e só muda publicando
         este cenário — o que sobe a versão e vence o rascunho. Não há mais "outra porta", e o
         rascunho do formato 2 vence em tudo, calado.
       · O rascunho do FORMATO 1 (gravado antes da 0032, quando o LGC mudava por outra porta) segue
         a regra do lote 4 até a 0033 (D25 = A): LOTE 4, item 6 ("Fica o digitado (Recomendado)"):
         o do rascunho FICA e, se ele for diferente do oficial, a tela adotada PERGUNTA qual manter,
         dizendo os dois (`ligar`). REVISÃO DO LOTE 4: a FOTOGRAFIA não é o digitado — se o LGC do
         rascunho é o oficial de quando ele nasceu (ninguém o digitou), vale o oficial atual, calado.
         Regra (e a decisão "só formato 1") em `lgc-rascunho.ts` (`lgcAoAbrir`), testada. */
  const decisaoLgc = lgcAoAbrir({
    formato: formatoDoRascunho,
    podePerguntar: editavel && !arquivado,
    doRascunho: empreendimento.lgcDisponivel,
    oficial: aberto.entrada.empreendimento.lgcDisponivel,
    oficialNaOrigem: lgcOrigem,
  });
  const origemLgc = decisaoLgc.origem;
  const lgcEmDisputa = decisaoLgc.emDisputa;
  const lgcCalado = decisaoLgc.calado;
  empreendimento = cadastroAoAbrir(empreendimento, aberto.entrada.empreendimento, lgcCalado);

  /* ---- o cabeçalho: o seletor no lugar do título, e a linha de publicação embaixo ---- */
  /* Os ativos que o seletor e as ações enxergam: a lista da abertura, trocada pela releitura
     de cada abertura do painel (um colega pode ter arquivado ou criado um cenário desde então). */
  let ativosAtuais: readonly CenarioResumo[] = listaCenarios;
  const acoes = acoesDeCenario({
    empresaId: cenario.empresaId,
    empreendimentoId: cenario.empreendimentoId,
    ativos: () => ativosAtuais,
    abertoId: cenarioId,
    /* APC-2: a versão DA TELA — a mesma que a sonda passa ao `temRascunhoNaoPublicado` */
    versaoDoAberto: () => cenarioCorrente.versao,
  });
  /* RELATÓRIO COMPARATIVO (lote de 09/10/2026, item 7 — EL-196): "Em cenários, crie um botão
     que me permite gerar um relatório trazendo os cenários que eu selecionar e uma análise
     crítica comparativa deles." O botão mora no rodapé do seletor (o lugar "dos cenários"
     desde o item 4) e tem um atalho na Comparação, que já chega com o que está marcado lá.
     Todos os perfis geram — é só leitura, e quem barra o acesso é a RLS. */
  const abrirRelatorio = (preselecionados: string[]): void => abrirDialogoRelatorio({
    empreendimentoId: cenario.empreendimentoId,
    preselecionados,
    editavel: editavel && !arquivado,
    marcarIds: perfil.isAdminHype,
  });
  const seletor = criarSeletorCenario({
    titulo: cenario.nome,
    abertoId: cenarioId,
    ativos: listaCenarios,
    relerAtivos: async () => {
      const lista = await listarCenarios(cenario.empreendimentoId);
      ativosAtuais = lista;
      return lista;
    },
    publicadaDoAberto: () => cenarioCorrente.versaoPublicada,
    poderes: { editar: editavel, excluirDefinitivo: perfil.isAdminHype, marcarIds: perfil.isAdminHype },
    acoes,
    /* A versão do ABERTO é a corrente (pode ter subido com uma publicação desta sessão);
       a dos outros é a da lista. É a régua do "rascunho vencido". */
    rascunhos: (ativos) =>
      editavel
        ? cenariosComRascunho(ativos.map((c) => (c.id === cenarioId ? { id: c.id, versao: cenarioCorrente.versao } : c)))
        : Promise.resolve(new Set<string>()),
    arquivados: () => listarCenariosArquivados(cenario.empreendimentoId),
    rodapeExtra: () => h('button', {
      class: 'selcen-rel',
      type: 'button',
      'data-el': 'EL-196',
      'data-acao': 'relatorio',
      title: 'Comparar de 2 a 5 cenários publicados num relatório pronto para imprimir',
      onclick: () => { seletor.fechar(); abrirRelatorio([]); },
    }, h('span', { html: icone.relatorio(15) }), 'Relatório comparativo'),
  });

  /* A linha "<empreendimento> · última publicação: vN" saiu da tela (lote 1 de 09/10/2026,
     item 3): o empreendimento já está na trilha, a versão no selo do seletor ("Publicado vN",
     inclusive a do cenário aberto) e "arquivado" na faixa abaixo. O cabeçalho guarda a versão
     só em ATRIBUTO — `data-versao-publicada` (e `data-arquivado`) —, que é o que as ferramentas
     de validação leem sem abrir o painel. Não é texto de tela. */
  const cabecalho = h('div', {
    class: 'cen-cab',
    'data-versao-publicada': String(cenario.versaoPublicada),
    'data-arquivado': arquivado,
  }, seletor.el);

  const faixaTexto = h('span', {});
  const faixa = h('div', { class: 'cen-faixa', role: 'status', hidden: true },
    h('span', { html: icone.alerta(16) }), faixaTexto);
  /* `copia` = a faixa é o recado da cópia não publicada; some quando ela for publicada. */
  let faixaDaCopia = false;
  const mostrarFaixa = (texto: string, ...extras: (Node | string)[]): void => {
    faixaTexto.replaceChildren(texto, ...extras);
    faixa.hidden = false;
    faixaDaCopia = false;
  };

  const corpo = h('div', {});
  corpo.appendChild(h('div', { class: 'pagehead' }, cabecalho));
  corpo.appendChild(faixa);

  const palco = h('div', { id: 'palco-simulador' });
  corpo.appendChild(palco);

  /* O cabeçalho guarda a versão publicada, e ele fica FORA do simulador — sem isto,
     publicar mudaria o servidor e o atributo continuaria dizendo a versão de antes. O que o
     usuário VÊ (o selo do seletor) lê `cenarioCorrente` e acompanha sozinho. */
  const atualizarCabecalho = (versaoPublicada: number): void => {
    cabecalho.dataset.versaoPublicada = String(versaoPublicada);
  };

  if (arquivado) {
    mostrarFaixa(
      'Este cenário está arquivado: não aceita alterações e não ocupa uma das 5 vagas. ',
      editavel
        ? h('button', {
            class: 'btn out sm',
            type: 'button',
            onclick: () => acoes.restaurar({
              id: cenario.id, nome: cenario.nome, versao: cenario.versao,
              versaoPublicada: cenario.versaoPublicada, arquivadoEm: cenario.arquivadoEm ?? '',
            }),
          }, h('span', { html: icone.restaurar(13) }), 'Restaurar')
        : 'Escolha outro cenário no seletor acima.',
    );
    seletor.liberar();
    return {
      trilha: trilhaDoCenario(cenario, nomeEmpresa, nomeEmp),
      corpo,
      ativo: 'empreendimentos',
      empresaId: cenario.empresaId,
      aoSair: () => { seletor.destruir(); fecharDialogoRelatorio(); },
    };
  }

  /* O que NÃO muda entre a montagem e cada atualização vinda da sonda. Uma fonte só:
     duas cópias divergiriam no dia em que alguém mexesse numa delas. */
  const contexto = {
    perfil: { isAdminHype: perfil.isAdminHype },
    /* O arquivo anexado é do EMPREENDIMENTO; o cenário viaja só como procedência. */
    documentos: {
      empresaId: cenario.empresaId,
      empreendimentoId: cenario.empreendimentoId,
      cenarioId,
    },
    publicacao: {
      /* Função, e não valor: depois de publicar, a versão sobe, e a PRÓXIMA publicação
         tem de mandar a nova. Um valor capturado aqui congelaria a versão de abertura e
         a segunda publicação bateria na trava sozinha. */
      cenario: () => cenarioCorrente,
      aoPublicar: (r: { versao: number; versaoPublicada: number }) => {
        cenarioCorrente = { ...cenarioCorrente, versao: r.versao, versaoPublicada: r.versaoPublicada };
        atualizarCabecalho(r.versaoPublicada);
        /* (os irmãos NÃO mudam: o cadastro publicado é só deste cenário — 0032) */
        /* o recado "a cópia ainda não foi publicada" deixou de ser verdade */
        if (faixaDaCopia) {
          faixa.hidden = true;
          faixaDaCopia = false;
        }
      },
    },
    /* o atalho "Relatório comparativo" da Comparação (o simulador chama pela ponte) */
    relatorio: { abrir: abrirRelatorio },
  };

  /* ---- EL-190: a sonda que traz a publicação de outra pessoa ---- */
  let sondando = false;
  let avisouDaVersaoNova = false;
  let avisouQueSaiu = false;
  /* `true` depois do `aoSair`: o que estava em voo (sessão, montagem, sonda) desiste. */
  let saiu = false;

  async function conferirVersaoPublicada(): Promise<void> {
    if (saiu || sondando || document.visibilityState === 'hidden') return;
    sondando = true;
    try {
      const v = await versaoDoCenario(cenarioId);
      if (v === null || saiu) return;

      /* Saiu daqui (arquivado por um colega, ou excluído de vez): DIZER, uma vez, e deixar
         dito na tela. O que eu digitei continua no rascunho — mas publicar não vai mais. */
      if (v.situacao !== 'ativo') {
        if (!avisouQueSaiu) {
          avisouQueSaiu = true;
          const texto = v.situacao === 'arquivado'
            ? 'Este cenário foi arquivado por alguém da equipe e não aceita mais publicação. Ele está em "Arquivados", no seletor acima, e pode ser restaurado.'
            : 'Este cenário não está mais disponível: foi arquivado ou excluído por outra pessoa. Escolha outro cenário no seletor acima.';
          mostrarFaixa(texto);
          avisar(texto, 'aviso');
        }
        return;
      }
      if (avisouQueSaiu) {
        /* Voltou (alguém restaurou): a faixa deixa de ser verdade. */
        avisouQueSaiu = false;
        faixa.hidden = true;
      }

      /* `<=` e não `!==`: publicar daqui já subiu `cenarioCorrente.versao` pelo
         `aoPublicar`, e a sonda não pode reagir ao próprio trabalho. */
      if (v.versao <= cenarioCorrente.versao) return;

      /* Trabalho meu não publicado ganha da novidade do outro — sempre. Trocar a tela
         aqui apagaria o que eu estava fazendo, e em silêncio. */
      /* revisão do lote 4: "vencido" contra a versão DA TELA — contra a do banco (que acabou de passar
         dela), todo rascunho já gravado parecia vencido e a guarda só segurava a edição ainda na fila */
      if (editavel && (await temRascunhoNaoPublicado(cenarioId, cenarioCorrente.versao))) {
        if (!avisouDaVersaoNova) {
          avisouDaVersaoNova = true;
          avisar(
            'Outra pessoa publicou uma versão mais nova deste cenário. Como você tem alterações que ainda não publicou, a tela continua como está — publique ou descarte o seu trabalho para ver a versão nova.',
            'aviso',
          );
        }
        return;
      }

      const novo = await abrirCenario(cenarioId);
      const levNovo = novo.entrada.lev;
      const premissasNovas = await carregarPremissas(novo.cenario.empresaId);
      /* A tela saiu no meio das idas ao servidor: `atualizar()` escreveria no simulador de OUTRA tela. */
      if (saiu) return;
      /* (A publicação do outro gravou o cadastro só DESTE cenário — 0032; os irmãos ficam como estão.) */
      const trocou = atualizar({
        empreendimento: novo.entrada.empreendimento,
        rascunho: paraLegado(novo.cenario, levNovo),
        cenarios: [paraLegado(novo.cenario, levNovo), ...irmaosOk],
        premissas: premissasNovas,
        vista: null, // a atualização preserva sozinha onde a pessoa está
        ...contexto,
      });
      /* Momento ruim (célula em edição, modal aberto, tabela em tela cheia): não insiste
         agora. `cenarioCorrente` fica como estava, então a próxima volta tenta de novo. */
      if (!trocou) return;

      cenarioCorrente = novo.cenario;
      /* Sem isto, a PRÓXIMA edição minha seria gravada apontando para a versão-base
         antiga — e `carregarRascunho` a descartaria por vencida, calado. */
      atualizarVersaoBase(cenarioId, novo.cenario.versao);
      atualizarCabecalho(novo.cenario.versaoPublicada);
      avisouDaVersaoNova = false;
      avisar('Este cenário acabou de ser atualizado com o que outra pessoa publicou.', 'ok');
    } catch {
      /* A sonda não é operação do usuário: falhar é ficar exatamente como está. */
    } finally {
      sondando = false;
    }
  }

  /* Voltar para a aba é o momento em que a desatualização mais incomoda — e é de graça. */
  const aoVoltarAFoco = (): void => {
    if (document.visibilityState === 'visible') void conferirVersaoPublicada();
  };
  let sonda: number | null = null;

  /* Revisão do lote 4 (10/10/2026): com o LGC oficial aplicado ("Usar o oficial", ou a fotografia trocada
     calada), o rascunho pode ter ficado IGUAL ao publicado — só o LGC diferia. Aí não há trabalho a
     recuperar: o rascunho sai (senão "Recuperamos o que você tinha digitado" voltava a cada F5 e aba nova,
     e o selo "alterações não publicadas" não sumia sem publicar). A comparação ignora a ordem das chaves
     (o jsonb reordena as do rascunho) e os ids de linha (a vaga criada na tela tem token; no banco,
     UUID). Sobrando outra edição: com a escolha da pessoa, o rascunho guarda o LGC escolhido (senão a
     pergunta voltaria na próxima abertura); calado, ele já é a fotografia. */
  async function semRascunhoSeIgualAoPublicado(): Promise<void> {
    if (mesmaEntrada({ empreendimento, lev }, aberto.entrada)) {
      restaurado = false;
      await descartarRascunho(cenarioId).catch(() => { /* rascunho que fica só repete o aviso */ });
    } else if (!lgcCalado) {
      anotarEdicao(cenarioId, structuredClone({ empreendimento, lev }));
    }
  }

  /* A vista foi ADOTADA: daqui em diante, o que se liga o `aoSair` desliga. Na ordem de antes:
     a sessão de rascunho (e a semente da cópia) ANTES de montar — cada edição feita depois da
     montagem precisa achar a sessão deste cenário. */
  async function ligar(): Promise<void> {
    try {
      let recadoDaCopia: string | null = null;
      if (editavel) {
        await iniciarRascunho({
          cenarioId,
          empresaId: cenario.empresaId,
          baseVersao: cenarioCorrente.versao,
        });
        if (saiu) return;
        /* SÓ PARA O RASCUNHO DO FORMATO 1 (D25 = A — `lgcEmDisputa` é nulo nos outros casos).
           LOTE 4 (item 6): o LGC do rascunho difere do oficial — a pessoa escolhe, ANTES de montar
           (a tela nasce com o valor escolhido; nada a trocar por baixo de um simulador montado).
           Esc, ✕, clique fora e o foco inicial ficam com "Manter o que digitei": o caminho que não
           desfaz nada. Escolher o oficial grava o oficial no rascunho — senão a pergunta voltaria
           na próxima abertura. "Manter" vale para esta aba: a mesma dupla de valores não é
           perguntada de novo ao voltar a este cenário. */
        let usarLgcOficial = false;
        if (lgcEmDisputa !== null && !lgcJaRespondido(cenarioId, lgcEmDisputa)) {
          /* revisão do lote 4: "Manter o que digitei" (e o caminho padrão nele) só quando se SABE que foi
             digitado. Rascunho antigo, sem a origem: rótulo neutro, e o caminho padrão — foco, destaque, Esc, ✕
             e clique fora (`padraoConfirmar`) — no OFICIAL. */
          const sabido = origemLgc === 'digitado';
          const t = textosDivergenciaLgc(lgcEmDisputa, moeda, sabido ? 'digitado' : 'desconhecida');
          usarLgcOficial = sabido
            ? await confirmar({
              titulo: t.titulo, corpo: t.corpo, confirmar: t.usarOficial, cancelar: t.manter, focarCancelar: true, escolha: true,
            })
            : await confirmar({
              titulo: t.titulo, corpo: t.corpo, confirmar: t.usarOficial, cancelar: t.manter, escolha: true, padraoConfirmar: true,
            });
          if (saiu) return;
          if (usarLgcOficial) {
            empreendimento = cadastroAoAbrir(empreendimento, aberto.entrada.empreendimento, true);
            if (semente === null) await semRascunhoSeIgualAoPublicado();
          } else {
            lembrarLgcRespondido(cenarioId, lgcEmDisputa);
          }
        } else if (lgcCalado && semente === null) {
          await semRascunhoSeIgualAoPublicado();
        }
        if (saiu) return;
        /* É AQUI que a semente da cópia vira o rascunho dela — só a tela adotada a consome. */
        const s = tomarSementeDeRascunho(cenarioId);
        if (s !== null) {
          anotarEdicao(cenarioId, structuredClone(s.entrada));
          await gravarAgora();
          recadoDaCopia = s.aviso;
        }
      }
      if (saiu) return;
      await montar(palco, {
        empreendimento,
        rascunho: paraLegado(cenario, lev),
        cenarios: [paraLegado(cenario, lev), ...irmaosOk],
        premissas,
        vista: vistaPorCenario.get(cenarioId) ?? null,
        ...contexto,
      });
      if (saiu) return;
      /* A área inicial (e o realce do item na barra lateral) agora vem de dentro da
         montagem, junto com a vista restaurada — era `trocarView('sim')` fixo aqui, e
         era ele que jogava a pessoa de volta às premissas a cada volta. */
      if (recadoDaCopia !== null || semente !== null) {
        /* A cópia recém-criada: o recado dela (POR QUE não foi publicada — e, no caso de faltar
           campo, quais) no lugar do "Recuperamos o que você tinha digitado", que seria falso —
           ninguém digitou nela. No aviso E na faixa: a lista de campos não se lê em 2 s. */
        const recado = recadoDaCopia ?? semente!.aviso;
        jaAvisadoDoRascunho.add(cenarioId);
        avisar(recado, 'aviso');
        mostrarFaixa(recado);
        faixaDaCopia = true;
      } else if (restaurado && !jaAvisadoDoRascunho.has(cenarioId)) {
        jaAvisadoDoRascunho.add(cenarioId);
        avisar('Recuperamos o que você tinha digitado e ainda não publicou.', 'aviso');
      }
      if (falharam > 0) {
        avisar(`${falharam} cenário(s) não puderam ser lidos e ficaram fora da comparação.`, 'aviso');
      }
    } catch (e) {
      if (saiu) return;
      palco.replaceChildren(
        h(
          'div',
          { class: 'card', style: 'padding:26px;display:grid;gap:12px;justify-items:center;text-align:center' },
          h('span', { style: 'color:hsl(var(--destructive))', html: icone.alerta(24) }),
          h('span', {}, e instanceof Error ? e.message : String(e)),
        ),
      );
    } finally {
      /* Só agora: a montagem varre `body > .ov`, e um diálogo do seletor aberto antes
         sumiria no meio (ver o cabeçalho de `ui/seletor-cenario.ts`). */
      if (!saiu) seletor.liberar();
    }
  }

  /* AS ÁREAS DO SIMULADOR.
     Elas existiam desde o porte e funcionavam — a varredura passava por todas —, mas
     chamando `trocarView` por código. Não havia botão: a barra lateral da v67, onde eles
     moravam, foi descartada e eu não repus. Da tela, só dava para ver a primeira.

     Os `id` são os mesmos da v67 (`tab-sim`, `tab-flc`, …) porque é assim que o
     `trocarView` do legado marca o item ativo, e `cmp-count` é onde o `renderContador`
     escreve quantos cenários há para comparar. Reusando os ids, realce e contador voltam
     sem tocar em uma linha do legado.

     QUEM VÊ O QUÊ (09/10/2026): Guiado (EL-155), Acompanhamento (EL-125) e Premissas macro
     (EL-097) são só do Admin Hype. Para os demais — gestor, analista e consulta — a barra
     tem só as duas áreas do trabalho: Análise de Demanda e Análise de Cenários. O grupo que
     ficaria sem item não é desenhado (um título solto sobre nada), e é por isso que cada
     grupo entra JUNTO com os seus itens em `soAdmin(...)`. Esconder o botão não basta: a vista
     lembrada também é guardada no legado (`restaurarVistaSimulador`). E não é permissão —
     quem decide é o banco (RLS); isto governa só o que aparece. */
  const areas = (): ItemNav[] => {
    const ir_ = (area: string) => () => {
      const w = window as unknown as { trocarView?: (a: string) => void };
      w.trocarView?.(area);
    };
    const soAdmin = (itens: ItemNav[]): ItemNav[] => (perfil.isAdminHype ? itens : []);
    return [
      ...soAdmin([
        { tipo: 'grupo', rotulo: 'Começar aqui' },
        { tipo: 'parent', id: 'tab-guiado', el: 'EL-155', rotulo: 'Guiado', icone: icone.info(17), aoClicar: ir_('guiado') },
      ]),
      { tipo: 'grupo', rotulo: 'Este cenário' },
      /* "Análise de Demanda" desde o lote 1 de 09/10/2026 (item 4; era "Premissas de Demanda").
         A etiqueta EL-093 é SÓ deste botão: a sub-aba "Composição" tem a dela (EL-199, item 5). */
      { tipo: 'sub', id: 'tab-sim', el: 'EL-093', rotulo: 'Análise de Demanda', aoClicar: ir_('sim') },
      { tipo: 'sub', id: 'tab-flc', el: 'EL-123', rotulo: 'Análise de Cenários', aoClicar: ir_('flc'), idContador: 'cmp-count' },
      ...soAdmin([
        { tipo: 'sub', id: 'tab-acomp', el: 'EL-125', rotulo: 'Acompanhamento', aoClicar: ir_('acomp') },
      ]),
      ...soAdmin([
        { tipo: 'grupo', rotulo: 'Configuração' },
        { tipo: 'parent', id: 'tab-prem', el: 'EL-097', rotulo: 'Premissas macro', icone: icone.grafico(17), aoClicar: ir_('prem') },
      ]),
    ];
  };

  return {
    navegacao: areas(),
    trilha: trilhaDoCenario(cenario, nomeEmpresa, nomeEmp),
    corpo,
    ativo: 'empreendimentos',
    /* É o caso que mais importa para o item "Documentos" da barra lateral: aqui dentro a
       barra ganha as áreas do simulador, e sem isto o item não saberia de quem são as
       pastas justamente na tela mais profunda. */
    empresaId: cenario.empresaId,
    /* ADOTADA: o palco já está no documento (o simulador procura os elementos dele por id). */
    aoMostrar: () => {
      lembrarUltimoCenario(cenario.empreendimentoId, cenarioId);
      sonda = window.setInterval(() => void conferirVersaoPublicada(), INTERVALO_SONDA_MS);
      document.addEventListener('visibilitychange', aoVoltarAFoco);
      void ligar();
    },
    /* A ordem importa: capturar ANTES de desmontar — depois de desmontar não há mais
       o que perguntar, e a resposta viria nula. */
    aoSair: () => {
      saiu = true;
      /* A sessão de rascunho deste cenário fecha com a tela (09/10/2026, revisão do lote) — e o
         `iniciarRascunho` dela ainda no ar desiste. Antes ninguém fechava: a próxima tela, se
         fosse só-leitura (o perfil Consulta; até a 0031, também o Admin olhando um cliente), não
         abria sessão nenhuma e herdava ESTA, e o `persist()` dela gravava a entrada do outro
         cenário no rascunho deste. O que estiver na fila continua e sobe. */
      encerrarRascunho(cenarioId);
      /* O seletor pendura ouvintes no documento enquanto o painel está aberto. */
      seletor.destruir();
      /* e o diálogo do relatório, se ficou aberto, sai com a tela */
      fecharDialogoRelatorio();
      /* A sonda morre com a tela. Sem isto ela continuaria perguntando pelo cenário que
         o usuário já fechou, e — pior — chamaria `atualizar()` sobre um simulador que
         não é mais este. */
      if (sonda !== null) window.clearInterval(sonda);
      sonda = null;
      document.removeEventListener('visibilitychange', aoVoltarAFoco);
      const vista = capturarVista();
      if (vista !== null) vistaPorCenario.set(cenarioId, vista);
      desmontar();
    },
  };
}

/**
 * Incorporadoras / <Incorporadora> / <Empreendimento> / **<Cenário>**.
 * O empreendimento é texto simples desde 09/10/2026: a tela que ele abria (a lista de
 * cenários) não existe mais — trocar de cenário é no seletor do título.
 */
function trilhaDoCenario(c: CenarioResumo, nomeEmpresa: string, nomeEmp: string): Vista['trilha'] {
  return [
    { texto: 'Incorporadoras', rota: { nome: 'incorporadoras' } },
    { texto: nomeEmpresa, rota: { nome: 'empreendimentos', empresaId: c.empresaId } },
    { texto: nomeEmp, simples: true },
    { texto: c.nome },
  ];
}
